<template>
  <div class="google-place-input">
    <input
      :id="resolvedInputId"
      ref="inputElement"
      :value="modelValue"
      type="text"
      class="form-input"
      :placeholder="placeholder"
      :maxlength="maxlength"
      :required="required"
      autocomplete="off"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="showSuggestions ? 'true' : 'false'"
      :aria-controls="listboxId"
      :aria-activedescendant="activeOptionId"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @keydown.down.prevent="moveActiveSuggestion(1)"
      @keydown.up.prevent="moveActiveSuggestion(-1)"
      @keydown.enter.prevent="selectActiveSuggestion"
      @keydown.esc="closeSuggestions"
    />

    <div
      v-if="showSuggestions"
      :id="listboxId"
      class="place-suggestions"
      role="listbox"
    >
      <button
        v-for="(suggestion, index) in suggestions"
        :id="getOptionId(index)"
        :key="suggestion.key"
        type="button"
        role="option"
        :aria-selected="activeIndex === index ? 'true' : 'false'"
        :class="['place-suggestion', { active: activeIndex === index }]"
        @pointerdown.prevent="selectSuggestion(suggestion)"
      >
        <span class="suggestion-main">{{ suggestion.mainText }}</span>
        <span v-if="suggestion.secondaryText" class="suggestion-secondary">
          {{ suggestion.secondaryText }}
        </span>
      </button>
    </div>

    <p
      v-else-if="statusMessage"
      class="place-status"
      role="status"
      aria-live="polite"
    >
      {{ statusMessage }}
    </p>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { GOOGLE_MAPS_API_KEY } from "@/config/api.js";

let googlePlacesPromise = null;
let googleMapsAuthFailed = false;
let previousGoogleMapsAuthFailure = null;
let googlePlaceInputSequence = 0;
let googlePlacesFailureLogged = false;

const logGooglePlacesFailureOnce = (stage) => {
  if (googlePlacesFailureLogged) return;
  googlePlacesFailureLogged = true;
  console.error(
    `[GooglePlaceInput] Google Places ${stage} failed; manual input remains available.`,
  );
};

const createGoogleMapsAuthError = () =>
  new Error(
    "Google Maps authentication failed. Check the API key, billing, enabled APIs, and HTTP referrer restrictions.",
  );

const hasGoogleMapsAuthFailed = () =>
  googleMapsAuthFailed || Boolean(window.__rabiitripGoogleMapsAuthFailed);

const installGoogleMapsAuthFailureHandler = (reject) => {
  if (!previousGoogleMapsAuthFailure && window.gm_authFailure) {
    previousGoogleMapsAuthFailure = window.gm_authFailure;
  }
  window.gm_authFailure = () => {
    googleMapsAuthFailed = true;
    window.__rabiitripGoogleMapsAuthFailed = true;
    if (typeof previousGoogleMapsAuthFailure === "function") {
      previousGoogleMapsAuthFailure();
    }
    reject(createGoogleMapsAuthError());
  };
};

const waitForGoogleMaps = (script) =>
  new Promise((resolve, reject) => {
    installGoogleMapsAuthFailureHandler(reject);
    if (hasGoogleMapsAuthFailed()) {
      reject(createGoogleMapsAuthError());
      return;
    }

    if (window.google?.maps?.importLibrary) {
      resolve(window.google.maps);
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      if (window.google?.maps?.importLibrary) {
        settled = true;
        resolve(window.google.maps);
      }
    };

    script.addEventListener("load", finish);
    script.addEventListener("error", () => {
      if (!settled) {
        settled = true;
        reject(new Error("Google Maps failed to load."));
      }
    });

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (window.google?.maps?.importLibrary) {
        window.clearInterval(timer);
        finish();
      } else if (Date.now() - startedAt > 10000) {
        window.clearInterval(timer);
        if (!settled) {
          settled = true;
          reject(new Error("Google Maps load timed out."));
        }
      }
    }, 100);
  });

const loadGooglePlaces = async () => {
  if (hasGoogleMapsAuthFailed()) {
    throw createGoogleMapsAuthError();
  }

  if (window.google?.maps?.importLibrary) {
    return window.google.maps.importLibrary("places");
  }

  if (!GOOGLE_MAPS_API_KEY) {
    throw new Error("Google Maps API key is not configured.");
  }

  if (!googlePlacesPromise) {
    googlePlacesPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        'script[data-rabiitrip-google-maps], script[src*="maps.googleapis.com/maps/api/js"]',
      );
      if (existingScript) {
        waitForGoogleMaps(existingScript)
          .then(() => window.google.maps.importLibrary("places"))
          .then(resolve)
          .catch(reject);
        return;
      }

      const script = document.createElement("script");
      const params = new URLSearchParams({
        key: GOOGLE_MAPS_API_KEY,
        libraries: "places",
        v: "weekly",
        loading: "async",
        language: "ja",
        region: "JP",
      });
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.defer = true;
      script.dataset.rabiitripGoogleMaps = "true";
      installGoogleMapsAuthFailureHandler(reject);
      script.onload = () => {
        if (hasGoogleMapsAuthFailed()) {
          reject(createGoogleMapsAuthError());
          return;
        }
        waitForGoogleMaps(script)
          .then(() => window.google.maps.importLibrary("places"))
          .then(resolve)
          .catch(reject);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  return googlePlacesPromise;
};

const getTextValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value.text === "string") return value.text;
  if (typeof value.toString === "function") {
    const text = value.toString();
    return text === "[object Object]" ? "" : text;
  }
  return "";
};

const normalizeSuggestion = (suggestion, index) => {
  const prediction = suggestion.placePrediction;
  const mainText =
    getTextValue(prediction?.mainText) ||
    getTextValue(prediction?.text) ||
    "候補";
  const secondaryText = getTextValue(prediction?.secondaryText);
  return {
    key: prediction?.placeId || `${mainText}-${index}`,
    raw: suggestion,
    prediction,
    mainText,
    secondaryText,
  };
};

export default {
  name: "GooglePlaceInput",
  props: {
    modelValue: { type: String, default: "" },
    inputId: { type: String, default: "" },
    placeholder: { type: String, default: "場所を入力して選択" },
    maxlength: { type: Number, default: 500 },
    required: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "place-selected", "manual-input"],
  setup(props, { emit }) {
    const instanceId = ++googlePlaceInputSequence;
    const resolvedInputId = computed(
      () => props.inputId || `google-place-input-${instanceId}`,
    );
    const listboxId = computed(() => `${resolvedInputId.value}-listbox`);
    const inputElement = ref(null);
    const suggestions = ref([]);
    const activeIndex = ref(-1);
    const hasFocus = ref(false);
    const placesLibrary = ref(null);
    const sessionToken = ref(null);
    const loadingFailed = ref(false);
    const isFetching = ref(false);
    const requestFailed = ref(false);
    const hasSearched = ref(false);
    let debounceTimer = null;
    let requestCounter = 0;

    const showSuggestions = computed(
      () => hasFocus.value && suggestions.value.length > 0,
    );
    const getOptionId = (index) =>
      `${resolvedInputId.value}-option-${index}`;
    const activeOptionId = computed(() =>
      showSuggestions.value && activeIndex.value >= 0
        ? getOptionId(activeIndex.value)
        : undefined,
    );

    const statusMessage = computed(() => {
      if (!hasFocus.value) return "";
      if (!props.modelValue?.trim()) return "";
      if (loadingFailed.value) {
        return "場所候補を読み込めません。住所や場所名をそのまま入力して続けられます。";
      }
      if (isFetching.value) return "候補を取得しています...";
      if (requestFailed.value) {
        return "場所候補を読み込めません。住所や場所名をそのまま入力して続けられます。";
      }
      if (hasSearched.value && suggestions.value.length === 0) {
        return "候補が見つかりませんでした。入力内容を変えてください。";
      }
      return "";
    });

    const ensurePlaces = async () => {
      if (loadingFailed.value) return null;
      if (placesLibrary.value) return placesLibrary.value;
      try {
        placesLibrary.value = await loadGooglePlaces();
        return placesLibrary.value;
      } catch {
        logGooglePlacesFailureOnce("initialization");
        loadingFailed.value = true;
        return null;
      }
    };

    const ensureSessionToken = async () => {
      const places = await ensurePlaces();
      const TokenClass =
        places?.AutocompleteSessionToken ||
        window.google?.maps?.places?.AutocompleteSessionToken;
      if (!TokenClass) return null;
      if (!sessionToken.value) {
        sessionToken.value = new TokenClass();
      }
      return sessionToken.value;
    };

    const closeSuggestions = () => {
      suggestions.value = [];
      activeIndex.value = -1;
    };

    const fetchSuggestions = async (input) => {
      const text = input.trim();
      if (!text || loadingFailed.value) {
        closeSuggestions();
        hasSearched.value = false;
        requestFailed.value = false;
        return;
      }

      const places = await ensurePlaces();
      const AutocompleteSuggestion =
        places?.AutocompleteSuggestion ||
        window.google?.maps?.places?.AutocompleteSuggestion;
      if (!AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
        logGooglePlacesFailureOnce("API availability check");
        loadingFailed.value = true;
        closeSuggestions();
        return;
      }

      const currentRequest = ++requestCounter;
      const token = await ensureSessionToken();
      try {
        isFetching.value = true;
        requestFailed.value = false;
        const { suggestions: fetchedSuggestions = [] } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: text,
            sessionToken: token,
          });
        if (currentRequest !== requestCounter) return;
        hasSearched.value = true;
        suggestions.value = fetchedSuggestions
          .filter((suggestion) => suggestion.placePrediction)
          .map(normalizeSuggestion);
        activeIndex.value = suggestions.value.length > 0 ? 0 : -1;
      } catch {
        logGooglePlacesFailureOnce("autocomplete request");
        requestFailed.value = true;
        hasSearched.value = true;
        closeSuggestions();
      } finally {
        if (currentRequest === requestCounter) {
          isFetching.value = false;
        }
      }
    };

    const scheduleFetchSuggestions = (value) => {
      if (debounceTimer) {
        window.clearTimeout(debounceTimer);
      }
      debounceTimer = window.setTimeout(() => {
        fetchSuggestions(value);
      }, 250);
    };

    const handleInput = (event) => {
      const value = event.target.value;
      emit("update:modelValue", value);
      emit("manual-input", value);
      if (!sessionToken.value) {
        ensureSessionToken();
      }
      scheduleFetchSuggestions(value);
    };

    const handleFocus = () => {
      hasFocus.value = true;
      if (props.modelValue) {
        scheduleFetchSuggestions(props.modelValue);
      }
    };

    const handleBlur = () => {
      window.setTimeout(() => {
        hasFocus.value = false;
        closeSuggestions();
      }, 150);
    };

    const useSuggestionAsManualInput = (suggestion) => {
      const value = suggestion?.mainText || props.modelValue || "";
      if (value) {
        emit("update:modelValue", value);
        emit("manual-input", value);
      }
      closeSuggestions();
      hasSearched.value = false;
      sessionToken.value = null;
    };

    const useSuggestionText = (suggestion) => {
      const value = suggestion?.mainText || props.modelValue || "";
      if (value) {
        emit("update:modelValue", value);
      }
      closeSuggestions();
      hasSearched.value = false;
    };

    const getSuggestionAddress = (suggestion) =>
      [suggestion?.mainText, suggestion?.secondaryText]
        .filter(Boolean)
        .join(" ");

    const getCoordinatesFromLocation = (location) => {
      const lat =
        typeof location?.lat === "function" ? location.lat() : location?.lat;
      const lng =
        typeof location?.lng === "function" ? location.lng() : location?.lng;
      const latitude = Number(lat);
      const longitude = Number(lng);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return null;
      }
      return { latitude, longitude };
    };

    const geocodeSuggestion = (suggestion, fallbackAddress) => {
      const maps = window.google?.maps;
      if (!maps?.Geocoder) return Promise.resolve(null);

      const request = suggestion?.prediction?.placeId
        ? { placeId: suggestion.prediction.placeId }
        : { address: fallbackAddress };
      if (!request.placeId && !request.address) return Promise.resolve(null);

      return new Promise((resolve) => {
        const geocoder = new maps.Geocoder();
        geocoder.geocode(request, (results, status) => {
          if (status !== "OK" || !results?.length) {
            resolve(null);
            return;
          }

          const result = results[0];
          const coordinates = getCoordinatesFromLocation(
            result.geometry?.location,
          );
          if (!coordinates) {
            resolve(null);
            return;
          }

          resolve({
            name: result.name || suggestion?.mainText || fallbackAddress,
            placeId:
              result.place_id || suggestion?.prediction?.placeId || null,
            address: result.formatted_address || fallbackAddress,
            ...coordinates,
          });
        });
      });
    };

    const emitSelectedPlace = (placeData) => {
      emit("update:modelValue", placeData.address);
      emit("place-selected", placeData);
      closeSuggestions();
      hasSearched.value = false;
      sessionToken.value = null;
    };

    const selectSuggestion = async (suggestion) => {
      useSuggestionText(suggestion);
      const fallbackAddress = getSuggestionAddress(suggestion) || props.modelValue;
      if (!suggestion?.prediction?.toPlace) {
        const geocodedPlace = await geocodeSuggestion(
          suggestion,
          fallbackAddress,
        );
        if (geocodedPlace) {
          emitSelectedPlace(geocodedPlace);
        } else {
          useSuggestionAsManualInput(suggestion);
        }
        return;
      }
      try {
        const place = suggestion.prediction.toPlace();
        let selectedPlace = null;
        if (!place?.fetchFields) {
          selectedPlace = await geocodeSuggestion(suggestion, fallbackAddress);
          if (selectedPlace) {
            emitSelectedPlace(selectedPlace);
          } else {
            useSuggestionAsManualInput(suggestion);
          }
          return;
        }

        await place.fetchFields({
          fields: ["id", "displayName", "formattedAddress", "location"],
        });

        const displayName =
          place.displayName?.text ||
          place.displayName ||
          suggestion.mainText ||
          props.modelValue ||
          "";
        const address = place.formattedAddress || displayName;
        const coordinates = getCoordinatesFromLocation(place.location);
        selectedPlace = coordinates
          ? {
              name: displayName,
              placeId: place.id || suggestion?.prediction?.placeId || null,
              address,
              ...coordinates,
            }
          : await geocodeSuggestion(suggestion, address);

        if (!selectedPlace) {
          useSuggestionAsManualInput(suggestion);
          return;
        }

        emitSelectedPlace({
          name: selectedPlace.name || displayName,
          placeId:
            selectedPlace.placeId ||
            place.id ||
            suggestion?.prediction?.placeId ||
            null,
          address,
          latitude: selectedPlace.latitude,
          longitude: selectedPlace.longitude,
        });
      } catch {
        logGooglePlacesFailureOnce("details request");
        const geocodedPlace = await geocodeSuggestion(
          suggestion,
          fallbackAddress,
        );
        if (geocodedPlace) {
          emitSelectedPlace(geocodedPlace);
        } else {
          useSuggestionAsManualInput(suggestion);
        }
      }
    };

    const moveActiveSuggestion = (direction) => {
      if (suggestions.value.length === 0) return;
      activeIndex.value =
        (activeIndex.value + direction + suggestions.value.length) %
        suggestions.value.length;
    };

    const selectActiveSuggestion = () => {
      if (activeIndex.value < 0) return;
      selectSuggestion(suggestions.value[activeIndex.value]);
    };

    watch(
      () => props.modelValue,
      (value) => {
        if (inputElement.value && inputElement.value.value !== value) {
          inputElement.value.value = value || "";
        }
      },
    );

    onBeforeUnmount(() => {
      if (debounceTimer) {
        window.clearTimeout(debounceTimer);
      }
    });

    return {
      inputElement,
      resolvedInputId,
      listboxId,
      activeOptionId,
      getOptionId,
      suggestions,
      activeIndex,
      showSuggestions,
      statusMessage,
      handleInput,
      handleFocus,
      handleBlur,
      closeSuggestions,
      moveActiveSuggestion,
      selectActiveSuggestion,
      selectSuggestion,
    };
  },
};
</script>

<style scoped>
.google-place-input {
  position: relative;
  width: 100%;
  z-index: 2;
}

.place-suggestions {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 10002;
  max-height: 260px;
  overflow-y: auto;
  background: white;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
}

.place-suggestion {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  width: 100%;
  min-height: 48px;
  padding: 0.625rem 0.875rem;
  border: none;
  border-bottom: 1px solid var(--color-border-light);
  background: white;
  color: var(--color-text);
  text-align: left;
  cursor: pointer;
}

.place-suggestion:last-child {
  border-bottom: none;
}

.place-suggestion:hover,
.place-suggestion.active {
  background: var(--primary-lightest);
}

.suggestion-main {
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.35;
}

.suggestion-secondary {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  line-height: 1.35;
}

.place-status {
  margin: 0.375rem 0 0;
  color: var(--color-text-muted);
  font-size: 0.75rem;
  line-height: 1.5;
}
</style>
